import { useCallback } from 'react'
import { useApplyCommandMutation } from './authoring'
import type { LinkPath } from '../model/link'

/**
 * Send one intent.
 *
 * Every write this client makes goes through here, which is what makes
 * **one intent per control** checkable rather than merely intended: there
 * is no batching call to reach for, and a control that wanted to emit two
 * would have to say so twice and produce two revisions.
 *
 * The actor is never a parameter. It is refused if sent — a client names
 * what it meant, and who did it comes from the credential, because the
 * audit trail is the thing a client-supplied actor would be forging.
 */
export function useIntent(tenant: string) {
  const [applyCommand, state] = useApplyCommandMutation()

  const send = useCallback(
    (id: string, intent: string, payload: Record<string, unknown>, path?: LinkPath) =>
      applyCommand({
        tenant,
        id,
        command: {
          intent,
          // Omitted rather than sent empty: an absent path addresses the
          // component's own document, which is what an empty array means
          // too, and sending `[]` states a value where none was chosen.
          ...(path && path.length > 0 ? { path } : {}),
          payload,
        },
      }).unwrap(),
    [applyCommand, tenant],
  )

  return { send, ...state }
}

/**
 * The catch for a send the screen does not wait on.
 *
 * `send` rejects on a refusal, because a caller that awaits it needs to
 * know. A control that fires and forgets — `onClick={() => send(…)}` —
 * has already drawn the refusal from the mutation's `error`, so there is
 * nothing left to do with the rejection but handle it. Left unhandled it
 * reached the window as an uncaught exception on every refused command,
 * under an alert that had already said the same thing correctly.
 */
export function shown(): void {}

/**
 * A target id as an intent's **payload** must spell it.
 *
 * Bare everywhere the contract can type it, and `tenant#value` inside a
 * command's untyped payload — because nothing can tell which of an
 * untyped object's members are ids to resolve against the path's tenant.
 * The one place a write and a read of the same field disagree, and it
 * goes away when the generator derives a schema per intent.
 *
 * Wrapped in a function so that the day it goes away, it goes away here.
 */
export function payloadId(tenant: string, id: string): string {
  return `${tenant}#${id}`
}
